/**
 * Workspace Actions for Actr Integration
 * 
 * Registers workspace CRUD actions with Actr orchestrator
 * 
 * CHANGE HISTORY:
 * - 251102_1110 [pending] danielbeer: Create workspace actions for Delivvr app
 */

import type { WorkspaceManager } from '../WorkspaceManager';
import type { WorkspaceConfig } from '../types/WorkspaceTypes';

/**
 * Register workspace actions with Actr
 * 
 * @param actr - Actr instance
 * @param workspaceManager - WorkspaceManager instance
 */
export function registerWorkspaceActions(actr: any, workspaceManager: WorkspaceManager): void {
  /**
   * Create workspace from template or config
   * 
   * @param params.template - Workspace template (Delivvr format)
   * @param params.config - Direct workspace config
   */
  actr.register('aggregatr:create-workspace', {
    handler: async (params: any) => {
      const { template, config } = params;
      
      if (template) {
        // Convert Delivvr template to WorkspaceConfig
        const workspaceConfig = convertTemplateToConfig(template);
        return await workspaceManager.createWorkspace(workspaceConfig);
      } else if (config) {
        return await workspaceManager.createWorkspace(config);
      } else {
        throw new Error('Either template or config must be provided');
      }
    },
    description: 'Create workspace from template or config',
    permissions: ['workspace:create']
  });

  /**
   * List all workspaces
   */
  actr.register('aggregatr:list-workspaces', {
    handler: async () => {
      return Array.from(workspaceManager['workspaces'].values());
    },
    description: 'List all workspaces',
    permissions: ['workspace:read']
  });

  /**
   * Get workspace by ID
   * 
   * @param params.workspaceId - Workspace ID
   */
  actr.register('aggregatr:get-workspace', {
    handler: async (params: any) => {
      const { workspaceId } = params;
      const workspace = workspaceManager['workspaces'].get(workspaceId);
      
      if (!workspace) {
        throw new Error(`Workspace ${workspaceId} not found`);
      }
      
      return workspace;
    },
    description: 'Get workspace by ID',
    permissions: ['workspace:read']
  });

  /**
   * Update workspace
   * 
   * @param params.workspaceId - Workspace ID
   * @param params.updates - Partial workspace config
   */
  actr.register('aggregatr:update-workspace', {
    handler: async (params: any) => {
      const { workspaceId, updates } = params;
      return await workspaceManager.updateWorkspace(workspaceId, updates);
    },
    description: 'Update workspace',
    permissions: ['workspace:write']
  });

  /**
   * Delete workspace
   * 
   * @param params.workspaceId - Workspace ID
   */
  actr.register('aggregatr:delete-workspace', {
    handler: async (params: any) => {
      const { workspaceId } = params;
      return await workspaceManager.deleteWorkspace(workspaceId);
    },
    description: 'Delete workspace',
    permissions: ['workspace:delete']
  });

  /**
   * Get dataset data
   * 
   * @param params.datasetId - Dataset ID
   * @param params.filters - Optional filters
   */
  actr.register('aggregatr:get-dataset-data', {
    handler: async (params: any) => {
      const { datasetId, filters } = params;
      
      // Get current workspace
      const state = workspaceManager['currentState'];
      if (!state) {
        throw new Error('No active workspace');
      }
      
      const workspace = workspaceManager['workspaces'].get(state.currentWorkspaceId);
      if (!workspace) {
        throw new Error('Current workspace not found');
      }
      
      const dataset = workspace.datasets.find(d => d.id === datasetId);
      if (!dataset) {
        throw new Error(`Dataset ${datasetId} not found`);
      }
      
      // For now, return empty array - will be implemented with real data sources
      // TODO: Implement data fetching based on dataset.source
      return [];
    },
    description: 'Get dataset data with optional filters',
    permissions: ['dataset:read']
  });

  /**
   * Update view mappings
   * 
   * @param params.viewId - View ID
   * @param params.mappings - Field mappings
   */
  actr.register('aggregatr:update-view-mappings', {
    handler: async (params: any) => {
      const { viewId, mappings } = params;
      
      // Get current workspace
      const state = workspaceManager['currentState'];
      if (!state) {
        throw new Error('No active workspace');
      }
      
      const workspace = workspaceManager['workspaces'].get(state.currentWorkspaceId);
      if (!workspace) {
        throw new Error('Current workspace not found');
      }
      
      const view = workspace.views.find(v => v.id === viewId);
      if (!view) {
        throw new Error(`View ${viewId} not found`);
      }
      
      // Update view mappings
      view.mappings = mappings;
      
      // Save workspace
      await workspaceManager.updateWorkspace(workspace.id, workspace);
      
      return view;
    },
    description: 'Update view field mappings',
    permissions: ['view:write']
  });
}

/**
 * Convert Delivvr workspace template to Aggregatr WorkspaceConfig
 */
function convertTemplateToConfig(template: any): Partial<WorkspaceConfig> {
  const now = new Date().toISOString();
  
  return {
    name: template.name,
    description: template.description,
    created: now,
    updated: now,
    
    // Convert datasets
    datasets: template.datasets?.map((ds: any) => ({
      id: generateId(),
      name: ds.name,
      description: ds.description,
      source: convertDataSource(ds.source),
      fields: ds.fields || [],
      created: now,
      updated: now
    })) || [],
    
    // Convert views
    views: template.views?.map((view: any) => ({
      id: generateId(),
      name: view.name,
      description: view.description,
      type: view.type,
      datasetId: view.datasetId, // Will need to map this
      config: view.config || {},
      mappings: view.mappings,
      created: now,
      updated: now
    })) || [],
    
    // Default mappings
    defaultMappings: template.mappings || {},
    
    // Integration settings
    integrations: template.integrations
  };
}

/**
 * Convert Delivvr DataSource to Aggregatr DataSourceConfig
 */
function convertDataSource(source: any): any {
  if (!source) {
    return { type: 'internal' };
  }
  
  return {
    type: source.type || 'internal',
    query: source.query,
    external: source.external
  };
}

/**
 * Generate unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
